import React, { createContext, useContext, useState, ReactNode } from 'react';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface WebSocketContextType {
  connectionStatus: ConnectionStatus;
  isConnected: boolean;
  totalUnreadCount: number;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setIsConnected: (connected: boolean) => void;
  setTotalUnreadCount: (count: number) => void;
  // Новые функции для WebSocket сообщений
  onUnreadCountUpdate: (botName: string, count: number) => void;
  onNewMessage: (message: any) => void;
  // Состояние для хранения последних сообщений
  lastUnreadCounts: { [botName: string]: number };
  lastNewMessages: { [botName: string]: any[] };
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [lastUnreadCounts, setLastUnreadCounts] = useState<{ [botName: string]: number }>({});
  const [lastNewMessages, setLastNewMessages] = useState<{ [botName: string]: any[] }>({});

  // Функция для обновления счетчиков непрочитанных
  const onUnreadCountUpdate = (botName: string, count: number) => {
    setLastUnreadCounts(prev => {
      const next = { ...prev, [botName]: count };
      const total = Object.values(next).reduce((sum, val) => sum + val, 0);
      setTotalUnreadCount(total);
      return next;
    });
  };

  // Функция для обработки новых сообщений
  const onNewMessage = (message: any) => {
    if (message.bot_name) {
      setLastNewMessages(prev => ({
        ...prev,
        [message.bot_name]: [...(prev[message.bot_name] || []), message]
      }));
    }
  };

  const value: WebSocketContextType = {
    connectionStatus,
    isConnected,
    totalUnreadCount,
    setConnectionStatus,
    setIsConnected,
    setTotalUnreadCount,
    onUnreadCountUpdate,
    onNewMessage,
    lastUnreadCounts,
    lastNewMessages
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
